import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getAllCompositeSegments, getClonedPlayerConfigFromClip } from '@/player-config/src/utils/clipUtils';
import { AddMenuItem } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/TimelinePlayerActionBar/AddMenu/types';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import { saveClipChanges, updateClipWithId, trackClipUpdate } from '@/stores/clip';
import { remotionPlayerStateStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import { getRelativePlayerTime } from '@/App/remotion/MainPlayer/utils/deletesUtils';
import { getPlayerTimeFromSegmentTime } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { getPlayerConfigElements } from '@/player-config/src/utils/playerConfigGetters';
import { brandKit } from '@/stores/brandKit';
import {
  insertElementIntoNewNonOverlappingTrack,
  updatePlayerConfigWithTracks,
  updateClipData,
  createTextElement
} from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';
import {
  buildCalloutStyles,
  DEFAULT_CALLOUT_DURATION_SEC
} from '@/Pages/Clip/ClipPlayer/ClipAssets/CalloutCaptions/calloutDefaults';
import { calculateContainerWidth } from '@/Pages/Clip/ClipPlayer/ClipAssets/CalloutCaptions/textUtils';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { SelectionOutlineAction } from '@/App/remotion/components/Outlines/types';
import { SelectionElementType } from '@/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { getFontFullPath } from '@/player-config/src/utils/fontUtils';

export const useCalloutCaptionAdd = () => {
  const { clipData, clipId, fps } = useClipsContext();

  const isDisabled = useMemo(() => {
    const compositeSegments = getAllCompositeSegments(clipData);
    const playerConfig = getClonedPlayerConfigFromClip({ ...clipData });
    return compositeSegments.length === 0 || !playerConfig;
  }, [clipData]);

  const getCurrentFrame = useCallback((): number => {
    const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
    return getRelativePlayerTime({ currentTime, deleteBounds: [] }) * fps - 1;
  }, [fps]);

  const onAdd = useCallback(async () => {
    const compositeSegments = getAllCompositeSegments(clipData);
    if (compositeSegments.length === 0) {
      return;
    }
    const { startTime, endTime, startSegmentId, endSegmentId } = transcriptTimelineSelectionStore.getSnapshot();
    const playerConfig = getClonedPlayerConfigFromClip({ ...clipData });
    if (!playerConfig) {
      return;
    }

    const currentFrame = getCurrentFrame();

    let startFrame: number;
    let durationFrames: number;

    if (startTime !== null && endTime !== null && startSegmentId && endSegmentId) {
      const playerStartTime = getPlayerTimeFromSegmentTime(startTime, clipData, startSegmentId);
      const playerEndTime = getPlayerTimeFromSegmentTime(endTime, clipData, endSegmentId);
      startFrame = Math.floor(playerStartTime * fps);
      durationFrames = Math.max(Math.floor((playerEndTime - playerStartTime) * fps), fps);
    } else {
      startFrame = Math.max(0, currentFrame - 3);
      durationFrames = DEFAULT_CALLOUT_DURATION_SEC * fps;
    }

    const text = 'Callout';
    const colors = brandKit.getSnapshot().colors;
    const brandColor = (colors && colors[0]) || '#FFFFFF';

    const captionFontLocation =
      clipData.asset_metadata.caption_styles?.font_location || clipData.asset_metadata.font_location;
    const fontFileName = captionFontLocation?.split('/').pop() || 'Poppins-Bold.ttf';
    const fontName = fontFileName.replace(/\.[^/.]+$/, '');

    const containerWidthPx = calculateContainerWidth(clipData.asset_metadata.size);

    const { containerStyle, style, transitions } = buildCalloutStyles(
      text,
      brandColor,
      fontName,
      'MANUAL-CREATION',
      containerWidthPx,
      clipData.asset_metadata.size
    );
    const id = uuidv4();
    const element = createTextElement({
      id,
      name: 'Callout',
      fromFrame: Math.max(0, startFrame),
      toFrame: startFrame + durationFrames,
      details: {
        text,
        font: {
          name: style.fontFamily || 'Poppins',
          location: captionFontLocation || getFontFullPath('Poppins-Bold.ttf')
        },
        areCaptionsHidden: true
      },
      containerStyle,
      style,
      transitions
    });

    const currentTracks = playerConfig?.playerConfig?.tracks ?? [];
    const elementTracks = getPlayerConfigElements(playerConfig);
    const updatedElementTracks = insertElementIntoNewNonOverlappingTrack(elementTracks, element as any);
    const otherTracks = currentTracks.filter(t => t.type !== 'ELEMENTS');
    const updatedPlayerConfig = updatePlayerConfigWithTracks(
      playerConfig as any,
      [...otherTracks, ...updatedElementTracks] as any
    );
    const updatedClip = updateClipData(clipData as any, updatedPlayerConfig as any);
    updateClipWithId(clipId, updatedClip);
    saveClipChanges({ clipId });
    trackClipUpdate(clipId, 'CALLOUT_CAPTION_ADD');

    EventBus.dispatch(CustomEvents.SelectionOutlineAction, {
      action: SelectionOutlineAction.SELECT,
      elementType: SelectionElementType.TEXT,
      data: { elementId: id, type: SelectionElementType.TEXT }
    });
  }, [clipData, clipId, fps, getCurrentFrame]);

  const addCalloutCaptionMenuItem: AddMenuItem = useMemo(
    () => ({
      id: 'add-callout-caption',
      label: 'Add Callout Caption',
      iconName: 'IconFileText',
      onClick: onAdd,
      isDisabled,
      showBetaTag: true
    }),
    [onAdd, isDisabled]
  );

  return { addCalloutCaptionMenuItem };
};
